import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { signedPercent, type Material } from '@zilian/contracts';
import { api } from '../api';
import { Empty, Heading, ListToolbar, Props, unitPrice } from '../ui';
import { editQuote } from './Products';

interface Point {
  d: string;
  s: string;
}
interface Benchmark {
  material: Material;
  name: string;
  exchange: string;
  contract: string;
  points: Point[];
  share: string | null;
  usable: boolean;
}
interface Overview {
  materials: Benchmark[];
  deadBand: string;
  products: any[];
}

const RANGES = [
  ['1M', '近 1 个月', 31],
  ['3M', '近 3 个月', 92],
  ['1Y', '近 1 年', 366],
] as const;
type Range = (typeof RANGES)[number][0];

const grouped = (v: string | number) => Number(v).toLocaleString('zh-CN');
const monthDay = (v: string) => `${Number(v.slice(5, 7))}/${Number(v.slice(8, 10))}`;
// Exchanges settle on trading days only, so the weekday explains a weekend or holiday gap.
const weekday = (v: string) => `周${'日一二三四五六'[new Date(`${v}T00:00:00`).getDay()]}`;
const percentText = (v: string | null) => (v ? `${Math.round(Number(v) * 100)}%` : '');

/** One material's latest settlement; the card is also the switch for the chart below. */
/** A price move as the domestic futures market colours it: red up, green down. */
const moveColor = (move: number) =>
  move > 0 ? 'error.main' : move < 0 ? 'success.main' : 'text.primary';

function BenchmarkCard(p: {
  b: Benchmark;
  linked: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const last = p.b.points.at(-1);
  const prev = p.b.points.at(-2);
  const move = last && prev ? Number(last.s) - Number(prev.s) : 0;
  return (
    <Card
      variant="outlined"
      sx={{
        borderWidth: 1.5,
        borderColor: p.selected ? 'primary.main' : 'divider',
        boxShadow: p.selected ? '0 0 0 3px #e8f3f0' : 'none',
        transition: 'border-color .15s, box-shadow .15s',
      }}
    >
      <CardActionArea aria-pressed={p.selected} onClick={p.onSelect} sx={{ px: 2.75, py: 2.5 }}>
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>
          {p.b.material} {p.b.name} · {p.b.exchange}
        </Typography>
        {last ? (
          <>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: 30,
                  fontWeight: 700,
                  letterSpacing: '-0.6px',
                  lineHeight: 1.25,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {grouped(last.s)}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                元/吨
              </Typography>
            </Box>
            <Typography
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
            >
              {monthDay(last.d)} {weekday(last.d)}结算 · 较前日{' '}
              <Box component="span" sx={{ color: moveColor(move), fontWeight: 600 }}>
                {move > 0 ? '+' : ''}
                {grouped(move)}
                {prev && `（${signedPercent(move / Number(prev.s))}）`}
              </Box>
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1.25, fontSize: 13 }}>
              {p.b.share ? (
                <>
                  联动{' '}
                  <Box component="b" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {p.linked}
                  </Box>{' '}
                  个产品 · 占成本 {percentText(p.b.share)}
                </>
              ) : (
                '未开启联动'
              )}
            </Typography>
          </>
        ) : (
          <>
            <Skeleton width={160} height={38} sx={{ mt: 1 }} />
            <Skeleton width={200} height={20} sx={{ mt: 0.5 }} />
          </>
        )}
      </CardActionArea>
    </Card>
  );
}

/** Settlement price over time: one series, so no legend; hover reads any day. */
function PriceChart(p: { points: Point[] }) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const height = 260;
  const pad = { top: 16, right: 16, bottom: 28, left: 56 };
  const values = p.points.map((x) => Number(x.s));
  // A rounded range with a little air, so the line never touches the frame.
  const { lo, hi, ticks } = useMemo(() => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const raw = (max - min || max * 0.02) / 4;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    const ticks = [];
    for (let v = lo; v <= hi + step / 2; v += step) ticks.push(v);
    return { lo, hi, ticks };
  }, [p.points]);
  const plotW = Math.max(10, width - pad.left - pad.right);
  const plotH = height - pad.top - pad.bottom;
  const x = (i: number) =>
    pad.left + (p.points.length > 1 ? (i / (p.points.length - 1)) * plotW : 0);
  const y = (v: number) => pad.top + (1 - (v - lo) / (hi - lo || 1)) * plotH;
  const path = values
    .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join('');
  const labelEvery = Math.max(1, Math.ceil(p.points.length / 6));
  const at = hover === null ? null : p.points[hover];
  const before = hover ? p.points[hover - 1] : null;

  const onMove = (event: React.PointerEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    setHover(Math.max(0, Math.min(p.points.length - 1, Math.round(ratio * (p.points.length - 1)))));
  };

  return (
    <Box ref={box} sx={{ position: 'relative', px: 1 }}>
      <svg width={width} height={height} role="img" aria-label="结算价走势">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={pad.left + plotW} y1={y(t)} y2={y(t)} stroke="#eef1f3" />
            <text
              x={pad.left - 10}
              y={y(t)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill="#6c7c83"
            >
              {grouped(t)}
            </text>
          </g>
        ))}
        {p.points.map((pt, i) =>
          i % labelEvery === 0 ? (
            <text
              key={pt.d}
              x={x(i)}
              y={height - 8}
              textAnchor={i === 0 ? 'start' : 'middle'}
              fontSize={11}
              fill="#6c7c83"
            >
              {monthDay(pt.d)}
            </text>
          ) : null,
        )}
        <path d={path} fill="none" stroke="#17665f" strokeWidth={2} strokeLinejoin="round" />
        {at && (
          <>
            <line
              x1={x(hover!)}
              x2={x(hover!)}
              y1={pad.top}
              y2={pad.top + plotH}
              stroke="#c5d0cd"
              strokeDasharray="3 3"
            />
            <circle
              cx={x(hover!)}
              cy={y(Number(at.s))}
              r={4.5}
              fill="#17665f"
              stroke="#fff"
              strokeWidth={2}
            />
          </>
        )}
        <rect
          x={pad.left}
          y={pad.top}
          width={plotW}
          height={plotH}
          fill="transparent"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      {at && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 8,
            left: Math.min(x(hover!) + 16, width - 170),
            px: 1.5,
            py: 1,
            pointerEvents: 'none',
            fontSize: 12,
            minWidth: 140,
          }}
        >
          <Box sx={{ color: 'text.secondary' }}>
            {at.d.replaceAll('-', '/')} {weekday(at.d)}结算
          </Box>
          <Box sx={{ fontSize: 15, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
            {grouped(at.s)} 元/吨
          </Box>
          {before && (
            <Box sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              较前日{' '}
              <Box
                component="span"
                sx={{ color: moveColor(Number(at.s) - Number(before.s)), fontWeight: 600 }}
              >
                {Number(at.s) >= Number(before.s) ? '+' : ''}
                {grouped(Number(at.s) - Number(before.s))}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

/**
 * Raw material benchmarks and how the enterprise's quotes follow them: the futures settlement
 * price of each material, its trend, and which sale items move with it by how much today.
 */
export function Materials(p: Props) {
  const [data, setData] = useState<Overview | null>(null);
  const [material, setMaterial] = useState<Material>('PP');
  const [range, setRange] = useState<Range>('3M');
  useEffect(() => {
    api<Overview>('/materials')
      .then(setData)
      .catch((e) => p.setError(e.message));
  }, [p.version, p.workspace]);

  const current = data?.materials.find((m) => m.material === material);
  const days = RANGES.find(([value]) => value === range)![2];
  const since = new Date(Date.now() - days * 86400000).toLocaleDateString('sv-SE');
  const points = current?.points.filter((x) => x.d >= since) ?? [];
  const products = data?.products ?? [];
  const linked = (m: Material) =>
    products.filter((s) => s.material === m && s.sales_price !== null);
  const rows = linked(material);

  const settings = () =>
    data &&
    p.open({
      title: '原料联动设置',
      description:
        '产品调整幅度 = 原料结算价较报价时的涨跌 × 原料占成本比例。不联动的原料留空。只影响新订单，已确认的订单不变。',
      fields: [
        ...data.materials.map((m) => ({
          name: m.material,
          label: `${m.material} 占产品成本（%）`,
          helper: m.contract,
          decimals: 1,
        })),
        {
          name: 'deadBand',
          label: '起调幅度（%）',
          helper: '涨跌不足此幅度时不调整',
          decimals: 1,
        },
      ],
      form: {
        ...Object.fromEntries(
          data.materials.map((m) => [m.material, m.share ? String(Number(m.share) * 100) : '']),
        ),
        deadBand: String(Number(data.deadBand) * 100),
      },
      submit: (form: any) => api('/materials/settings', form),
    });

  return (
    <>
      <Heading
        title="原料行情"
        subtitle="期货主力合约每日结算价，销售报价按它的涨跌联动调整"
        action={
          <Button variant="outlined" onClick={settings} disabled={!data}>
            联动设置
          </Button>
        }
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
          mb: 2.5,
        }}
      >
        {(data?.materials ?? []).map((b) => (
          <BenchmarkCard
            key={b.material}
            b={b}
            linked={linked(b.material).length}
            selected={b.material === material}
            onSelect={() => setMaterial(b.material)}
          />
        ))}
        {!data && [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={150} />)}
      </Box>

      {current && (
        <Paper sx={{ overflow: 'hidden', mb: 2.5 }}>
          <ListToolbar>
            <Box sx={{ pl: 1, py: 1.5 }}>
              <Typography component="h2" sx={{ fontSize: 15, fontWeight: 650 }}>
                {current.exchange}
                {current.contract}结算价
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                元/吨 · 每个交易日收盘后更新 · 数据经新浪财经转载交易所行情
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              value={range}
              onChange={(e) => setRange(e.target.value as Range)}
              sx={{ width: 130 }}
            >
              {RANGES.map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </ListToolbar>
          {!current.usable && (
            <Alert severity="warning" sx={{ mx: 3, mt: 2 }}>
              近 10 天没有新的结算价，暂停按{current.material}联动调整，恢复更新后自动继续。
            </Alert>
          )}
          {points.length > 1 ? (
            <Box sx={{ py: 2 }}>
              <PriceChart points={points} />
            </Box>
          ) : (
            <Empty>暂无行情数据</Empty>
          )}
        </Paper>
      )}

      {data && (
        <Paper sx={{ overflow: 'hidden' }}>
          <div className="panel-title">
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 650 }}>
                按 {material} 联动的产品
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                {current?.share
                  ? `新订单按当前调整幅度计入原料调整，单价保持报价不变。涨跌不足 ${percentText(data.deadBand)} 不调整。`
                  : `${material} 还未开启联动，在「联动设置」填写占成本比例后生效。`}
              </Typography>
            </Box>
          </div>
          {rows.length ? (
            <Table
              sx={{
                '& th, & td': { px: 1.5 },
                '& th:first-of-type, & td:first-of-type': { pl: 3 },
              }}
            >
              <TableHead>
                <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableCell>产品</TableCell>
                  <TableCell align="right">销售报价</TableCell>
                  <TableCell align="right">报价时结算价</TableCell>
                  <TableCell align="right">当前调整</TableCell>
                  <TableCell sx={{ pr: 3 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{s.name}</Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                        {[s.code, s.spec].filter(Boolean).join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {unitPrice(s.sales_price)} / {s.unit}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {s.material_base ? (
                        <>
                          {grouped(s.material_base)}
                          <Box component="span" sx={{ color: 'text.secondary', ml: 0.75 }}>
                            {monthDay(s.material_base_date)}
                          </Box>
                        </>
                      ) : (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          待行情更新后起算
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: 650,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.adjust ? (
                        <>
                          {signedPercent(s.adjust.rate)}
                          <Box
                            component="span"
                            sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}
                          >
                            原料 {signedPercent(s.adjust.change)}
                          </Box>
                        </>
                      ) : (
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                          不调整
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 3, whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        onClick={() => editQuote(p, { ...s, salesPrice: s.sales_price })}
                      >
                        修改报价
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Empty>没有按 {material} 联动的销售产品</Empty>
          )}
        </Paper>
      )}
    </>
  );
}
